import { CP_AdvSiteName, CP_EmbedType } from "@mp-commons/modules/ads/adcall/CustomParams.js";
import { AdBreakType, AdUnitType, VASTTagAdUnit } from "@mp-commons/modules/ads/AdUnit.js";

/**
 * Interface for providing product ads in the form of VAST tag ad units.
 * 
 * Implementations should return arrays of ad units for different ad positions:
 * - Prerolls: Ads shown before the main content.
 * - Sprerolls: Special preroll ads, potentially with different logic or sources.
 * - Postrolls: Ads shown after the main content.
 * 
 * @remarks
 * The `embedType` parameter allows customization of ad selection based on the embedding context.
 */
export interface ProductAdsProvider {
  prerolls(embedType?: CP_EmbedType): VASTTagAdUnit[];
  sprerolls(embedType?: CP_EmbedType): VASTTagAdUnit[];
  postrolls(embedType?: CP_EmbedType): VASTTagAdUnit[];
}

/**
 * Builds a Google Ad Manager VAST ad call URL for the specified ad unit key.
 *
 * @param adUnitKey - The key identifying the ad unit to request ads for.
 * @returns The constructed ad call URL as a string.
 */
function adCallBuilder(adUnitKey: string): string {
  const output = "vast";
  return `https://pubads.g.doubleclick.net/gampad/ads?sz=640x480&iu=/1010633/${adUnitKey}&tfcd=0&npa=0&gdfp_req=1&output=${output}&unviewed_position_start=1&env=vp&impl=s&url=[referrer_url]&correlator=[timestamp]&cust_params=[params]`;
}

/**
 * Builds a unique ad unit key string based on the provided unit type, root, and optional child unit.
 *
 * @param unit - The type of ad unit (e.g., "preroll", "midroll", "midroll2", "postroll", "tint").
 * @param root - The root identifier for the ad unit.
 * @param childUnit - An optional child unit identifier to further specify the ad unit.
 * @returns A string representing the constructed ad unit key.
 */
function adUnitKey(
  unit: "preroll" | "midroll" | "midroll2" | "postroll" | "tint",
  root: string,
  childUnit?: string
): string {
  const base = `${root}_${unit}`;
  return childUnit ? `${base}/${base}_${childUnit}` : base;
}

/**
 * Builds a VAST tag ad unit object using the provided base URL, label, and ad break type.
 *
 * @param baseUrl - The base URL for the ad unit.
 * @param label - A label describing the ad unit.
 * @param breakType - The type of ad break (e.g., pre-roll, mid-roll, post-roll).
 * @returns A `VASTTagAdUnit` object containing the specified properties and a unit type of `VAST_TAG`.
 */
function buildAdUnit(baseUrl: string, label: string, breakType: AdBreakType): VASTTagAdUnit {
  return { baseUrl, label, breakType, unitType: AdUnitType.VAST_TAG };
}

const defaultFpPreroll = buildAdUnit(adCallBuilder("video_preroll"), "preroll", AdBreakType.PREROLL);
const defaultFpSpreroll = buildAdUnit(adCallBuilder("video_spreroll"), "spreroll", AdBreakType.PREROLL);
const defaultFpSpreroll2 = buildAdUnit(adCallBuilder("video_midroll2"), "midroll2", AdBreakType.PREROLL);
const defaultFpPostroll = buildAdUnit(adCallBuilder("video_postroll"), "postroll", AdBreakType.POSTROLL);
const rugbyPreroll = buildAdUnit(adCallBuilder("video_test"), "preroll", AdBreakType.PREROLL);

const DEFAULT_PREROLLS: VASTTagAdUnit[] = [defaultFpPreroll];
const DEFAULT_MIDROLLS: VASTTagAdUnit[] = [defaultFpSpreroll, defaultFpSpreroll2];
const DEFAULT_POSTROLLS: VASTTagAdUnit[] = [defaultFpPostroll];


/**
 * Builds an array containing a single ad unit for the "tint" ad break.
 *
 * @param root - The root identifier used to generate the ad unit key.
 * @param opts - Optional parameters for the ad unit.
 * @param opts.child - An optional child identifier to further specify the ad unit key.
 * @returns An array with one ad unit configured for a "tint_preroll" ad break.
 */
function tintKit(
  root: string,
  opts?: { child?: string }
) {
  return [
    buildAdUnit(adCallBuilder(adUnitKey("tint", root, opts?.child)), "tint_preroll", AdBreakType.PREROLL),
  ]
}

/**
 * Builds a list containing prerolls VAST ad unit.
 *
 * @param root - The root identifier for the ad unit.
 * @param opts - Optional parameters for the ad unit.
 * @param opts.child - An optional child identifier for the ad unit.
 * @returns An array containing one VASTTagAdUnit configured as a preroll ad break.
 */
function prerollKit(
  root: string,
  opts?: { child?: string }
): VASTTagAdUnit[] {
  return [
    buildAdUnit(
      adCallBuilder(adUnitKey("preroll", root, opts?.child)),
      "preroll",
      AdBreakType.PREROLL
    ),
  ];
}

/**
 * Builds an array containing midrolls VAST ad unit.
 *
 * Even if the adUnit is named "midroll" defacto these are used as prerolls
 *
 * @param root - The root identifier used to build ad unit keys.
 * @param opts - Optional parameters for ad unit construction.
 * @param opts.child - An optional child identifier to further specify the ad unit key.
 * @param opts.occurrences - Number of midroll units to include (max 2).
 * @returns An array of VASTTagAdUnit objects representing midroll ad units.
 */
function midrollKit(
  root: string,
  opts: { child?: string, occurrences: number }
): VASTTagAdUnit[] {
  const midrolls: VASTTagAdUnit[] = [];
  if (opts.occurrences >= 1) {
    midrolls.push(buildAdUnit(adCallBuilder(adUnitKey(`midroll`, root, opts.child)), `spreroll`, AdBreakType.PREROLL));
  }
  if (opts.occurrences >= 2) {
    midrolls.push(buildAdUnit(adCallBuilder(adUnitKey(`midroll2`, root, opts.child)), `midroll2`, AdBreakType.PREROLL));
  }
  return midrolls;
}

/**
 * Builds an array containing postrolls VAST ad unit.
 *
 * @param root - The root identifier for the ad unit.
 * @param opts - Optional parameters for the ad unit.
 * @param opts.child - An optional child identifier to further specify the ad unit.
 * @returns An array of `VASTTagAdUnit` objects representing the postroll ad unit.
 */
function postrollKit(
  root: string,
  opts?: { child?: string }
): VASTTagAdUnit[] {
  return [
    buildAdUnit(adCallBuilder(adUnitKey("postroll", root, opts?.child)), "postroll", AdBreakType.POSTROLL),
  ];
}

/**
 * Represents the buckets for different ad break types in a media player.
 * 
 * - `PREROLL`: Optional array of VASTTagAdUnit objects to be played before the main content.
 * - `MIDROLL`: Optional array of VASTTagAdUnit objects to be played during the main content.
 * - `POSTROLL`: Optional array of VASTTagAdUnit objects to be played after the main content.
 */
type BreakBuckets = {
  PREROLL?: VASTTagAdUnit[];
  MIDROLL?: VASTTagAdUnit[];
  POSTROLL?: VASTTagAdUnit[];
};

/**
 * List of embed types that do not support fallback mechanisms.
 * 
 * This array contains embed types for which fallback options are not available or not implemented.
 * Used to determine behavior when an embed type fails to load or initialize.
 *
 * @see CP_EmbedType
 */
const embedTypeWithoutFallback: CP_EmbedType[] = [CP_EmbedType.VIDEO_TINT];


/**
 * Builds a product ads provider that selects ad break buckets based on embed type and break type.
 *
 * The provider uses the following logic for each break type and embed type:
 * - If an override is provided and non-empty for the embed type, it is used.
 * - If the embed type is in the `embedTypeWithoutFallback` list and no override is present, returns an empty array (no fallback).
 * - Otherwise, falls back to the base break buckets, or to defaults if base is empty.
 *
 * @param opts - Configuration options for the ads provider.
 * @param opts.base - The base break buckets to use as fallback for all embed types except those explicitly excluded.
 * @param opts.byEmbedType - Optional overrides for break buckets per embed type.
 * @returns A `ProductAdsProvider` with methods to retrieve preroll, midroll, and postroll ad units for a given embed type.
 */
function buildAdsProvider(opts: {
  base: Required<BreakBuckets>; // fallback NON PER TINT
  byEmbedType?: Partial<Record<CP_EmbedType, BreakBuckets>>;
}): ProductAdsProvider {
  const defaults = {
    PREROLL: DEFAULT_PREROLLS,
    MIDROLL: DEFAULT_MIDROLLS,
    POSTROLL: DEFAULT_POSTROLLS,
  };

  const byET = opts.byEmbedType ?? {};


  /**
   * Determines whether the provided embed type should fall back to the base option.
   *
   * @param et - The embed type to check.
   * @returns `true` if the embed type is not in the `embedTypeWithoutFallback` array, indicating fallback is allowed; otherwise, `false`.
   */
  const fallbackOnBase = (et: CP_EmbedType) => { return embedTypeWithoutFallback.indexOf(et) < 0 };


  /**
   * Selects a list of VASTTagAdUnit items based on the provided break type and optional embed type.
   *
   * The selection process follows these steps:
   * 1. If an `embedType` is provided, attempts to retrieve an override list from `byET`.
   *    - If an override exists and is non-empty, it is returned.
   *    - If `fallbackOnBase(embedType)` returns `false`, an empty array is returned, blocking fallback for certain embed types.
   * 2. If no suitable override is found or no `embedType` is provided, attempts to retrieve a base list from `opts.base`.
   *    - If the base list exists and is non-empty, it is returned.
   * 3. If neither an override nor a base list is found, returns the default list for the given break type.
   *
   * @param bt - The break type key used to select the ad units.
   * @param embedType - (Optional) The embed type used to determine overrides and fallback behavior.
   * @returns An array of `VASTTagAdUnit` items corresponding to the selection logic.
   */
  const pick = (bt: keyof Required<BreakBuckets>, embedType: CP_EmbedType): VASTTagAdUnit[] => {
    if (embedType) {
      const override = byET[embedType]?.[bt];
      if (override && override.length > 0) return override;
      if (!fallbackOnBase(embedType)) return []; // blocca il fallback per determinati embedTypes
    }
    const baseList = opts.base[bt];
    if (baseList && baseList.length > 0) return baseList;
    return defaults[bt] ?? [];
  };

  return {
    prerolls(embedType) {
      return pick("PREROLL", embedType);
    },

    sprerolls(embedType) {
      return pick("MIDROLL", embedType);
    },

    postrolls(embedType) {
      return pick("POSTROLL", embedType);
    }
  };
}

// =============================== BUILDERS SECTION  =================================


/**
 * Creates and returns a `ProductAdsProvider` configuration for the "cookist_it" product.
 *
 * This provider defines ad break buckets for preroll, midroll, and postroll positions,
 * and customizes preroll and postroll buckets based on the embed type.
 *
 * @returns {ProductAdsProvider} The configured ads provider for "cookist_it".
 */
function providerCOOKIST(): ProductAdsProvider {

  const base: Required<BreakBuckets> = {
    PREROLL: prerollKit("cookist_it"),
    MIDROLL: midrollKit("cookist_it", { occurrences: 1 }), // Only one midroll (spreroll) for cookist_it
    POSTROLL: postrollKit("cookist_it"),
  };

  const byEmbedType: Partial<Record<CP_EmbedType, BreakBuckets>> = {
    [CP_EmbedType.VIDEO_TESTO]: { PREROLL: prerollKit("cookist_it", { child: CP_EmbedType.VIDEO_TESTO }), POSTROLL: postrollKit("cookist_it", { child: CP_EmbedType.VIDEO_TESTO }) },
    [CP_EmbedType.VIDEO]: { PREROLL: prerollKit("cookist_it", { child: CP_EmbedType.VIDEO }), POSTROLL: postrollKit("cookist_it", { child: CP_EmbedType.VIDEO }) },
    [CP_EmbedType.GALLERY]: { PREROLL: prerollKit("cookist_it", { child: CP_EmbedType.GALLERY }), POSTROLL: postrollKit("cookist_it", { child: CP_EmbedType.GALLERY }) },
    [CP_EmbedType.FANPLAYER]: { PREROLL: prerollKit("cookist_it", { child: CP_EmbedType.FANPLAYER }), POSTROLL: postrollKit("cookist_it", { child: CP_EmbedType.FANPLAYER }) },
    [CP_EmbedType.EMBED]: { PREROLL: prerollKit("cookist_it", { child: CP_EmbedType.EMBED }), POSTROLL: postrollKit("cookist_it", { child: CP_EmbedType.EMBED }) },
  };

  return buildAdsProvider({ base, byEmbedType });
}

/**
 * Creates and returns a `ProductAdsProvider` configured for the "cookist" provider.
 *
 * @returns {ProductAdsProvider} The configured ads provider for "cookist".
 */
function providerCOOKIST_WOW(): ProductAdsProvider {

  const base: Required<BreakBuckets> = {
    PREROLL: prerollKit("cookist"),
    MIDROLL: midrollKit("cookist", { occurrences: 2 }), // Two midrolls (spreroll & midroll2) 
    POSTROLL: postrollKit("cookist"),
  };

  return buildAdsProvider({ base });
}

/**
 * Creates and returns a `ProductAdsProvider` configured for the "cookist_leg" provider.
 *
 * @returns {ProductAdsProvider} The configured ads provider for "cookist_leg".
 */
function providerCOOKIST_LEGAL(): ProductAdsProvider {
  const base: Required<BreakBuckets> = {
    PREROLL: prerollKit("cookist_leg"),
    MIDROLL: midrollKit("cookist_leg", { occurrences: 2 }), // Two midrolls (spreroll & midroll2) 
    POSTROLL: postrollKit("cookist_leg"),
  };
  return buildAdsProvider({ base });
}

/**
 * Creates and returns a `ProductAdsProvider` configured for the "ohga" provider.
 *
 * @returns {ProductAdsProvider} The configured ads provider for "ohga".
 */
function providerOHGA(): ProductAdsProvider {
  const base: Required<BreakBuckets> = {
    PREROLL: prerollKit("ohga_video/ohga"),
    MIDROLL: midrollKit("ohga_video/ohga", { occurrences: 2 }), // Two midrolls (spreroll & midroll2) 
    POSTROLL: postrollKit("ohga_video/ohga"),
  };
  return buildAdsProvider({ base });
}

/**
 * Creates and returns a `ProductAdsProvider` configured for the "kodami" provider.
 *
 * @returns {ProductAdsProvider} The configured ads provider for "kodami".
 */
function providerKODAMI(): ProductAdsProvider {
  const base: Required<BreakBuckets> = {
    PREROLL: prerollKit("kodami"),
    MIDROLL: midrollKit("kodami", { occurrences: 2 }), // Two midrolls (spreroll & midroll2) 
    POSTROLL: postrollKit("kodami"),
  };
  return buildAdsProvider({ base });
}


/**
 * Creates and returns a `ProductAdsProvider` configured for the "geopop" provider.
 *
 * @returns {ProductAdsProvider} The configured ads provider for "geopop".
 */
function providerGEOPOP(): ProductAdsProvider {
  const base: Required<BreakBuckets> = {
    PREROLL: prerollKit("geopop"),
    MIDROLL: midrollKit("geopop", { occurrences: 2 }), // Two midrolls (spreroll & midroll2) 
    POSTROLL: postrollKit("geopop"),
  };
  return buildAdsProvider({ base });
}



/**
 * Creates a `ProductAdsProvider` configuration for the "wamily" provider.
 *
 * The base configuration includes PREROLL, MIDROLL, and POSTROLL ad break buckets,
 * each initialized with their respective kits for "wamily".
 * For the `VIDEO_TINT` embed type, overrides the PREROLL bucket with a specialized tint kit.
 *
 * @returns {ProductAdsProvider} The ads provider configuration for "wamily".
 */
function providerWAMILY(): ProductAdsProvider {
  const base: Required<BreakBuckets> = {
    PREROLL: prerollKit("wamily"),
    MIDROLL: midrollKit("wamily", { occurrences: 2 }), // Two midrolls (spreroll & midroll2) 
    POSTROLL: postrollKit("wamily"),
  };
  const byEmbedType: Partial<Record<CP_EmbedType, BreakBuckets>> = {
    [CP_EmbedType.VIDEO_TINT]: { PREROLL: tintKit("wamily") },
  };
  return buildAdsProvider({ base, byEmbedType });
}

/**
 * Creates a `ProductAdsProvider` configuration for the "lexplain" provider.
 *
 * The base configuration includes PREROLL, MIDROLL, and POSTROLL ad break buckets,
 * each initialized with their respective kits for "lexplain".
 * For the `VIDEO_TINT` embed type, overrides the PREROLL bucket with a specialized tint kit.
 *
 * @returns {ProductAdsProvider} The ads provider configuration for "lexplain".
 */
function providerLEXPLAIN(): ProductAdsProvider {
  const base: Required<BreakBuckets> = {
    PREROLL: prerollKit("lexplain"),
    MIDROLL: midrollKit("lexplain", { occurrences: 2 }), // Two midrolls (spreroll & midroll2) 
    POSTROLL: postrollKit("lexplain"),
  };
  const byEmbedType: Partial<Record<CP_EmbedType, BreakBuckets>> = {
    [CP_EmbedType.VIDEO_TINT]: { PREROLL: tintKit("lexplain") },
  };
  return buildAdsProvider({ base, byEmbedType });
}



/**
 * Creates and returns a `ProductAdsProvider` configured for the "fanpage" provider.
 *
 * @returns {ProductAdsProvider} The configured ads provider for "fanpage".
 */
function providerFANPAGE(): ProductAdsProvider {
  const base: Required<BreakBuckets> = {
    PREROLL: DEFAULT_PREROLLS,
    MIDROLL: DEFAULT_MIDROLLS,
    POSTROLL: DEFAULT_POSTROLLS,
  };
  return buildAdsProvider({ base });
}

/**
 * Returns the appropriate `ProductAdsProvider` implementation based on the given `CP_AdvSiteName` product.
 *
 * @param product - The product identifier of type `CP_AdvSiteName` for which to retrieve the ads provider.
 * @returns The corresponding `ProductAdsProvider` for the specified product. If the product is not recognized, defaults to the FANPAGE provider.
 */
export function productAdsProvider(product: CP_AdvSiteName): ProductAdsProvider {
  switch (product) {
    case CP_AdvSiteName.LEXPLAIN:
      return providerLEXPLAIN();
    case CP_AdvSiteName.COOKISTIT:
      return providerCOOKIST();
    case CP_AdvSiteName.COOKISTWOW:
      return providerCOOKIST_WOW();
    case CP_AdvSiteName.COOKISTLEGAL:
      return providerCOOKIST_LEGAL();
    case CP_AdvSiteName.OHGA:
      return providerOHGA();
    case CP_AdvSiteName.KODAMI:
      return providerKODAMI();
    case CP_AdvSiteName.GEOPOP:
      return providerGEOPOP();
    case CP_AdvSiteName.WAMILY:
      return providerWAMILY();
    case CP_AdvSiteName.FANPAGE:
      return providerFANPAGE();
    default:
      return providerFANPAGE();
  }
}


